type Callback = (data: any) => void

/**
 * Класс EventEmmiter представляет собой простую реализацию механизма
 * для работы с событиями. Он позволяет добавлять, удалять и вызывать
 * обработчики событий, а также удалять все обработчики события.
 *
 * @example
 * // Создаем экземпляр класса EventEmmiter
 * const emitter = new EventEmmiter();
 *
 * // Добавляем обработчик события "click"
 * emitter.on("click", (data: any) => {
 *   console.log("Click event occurred with data:", data);
 * });
 *
 * // Вызываем событие "click"
 * emitter.emit("click", "Button clicked");
 * // Output: Click event occurred with data: Button clicked
 *
 * // Задаем обработчик, который будет вызван только один раз при наступлении события "hover"
 * emitter.once("hover", (data: any) => {
 *   console.log("Hover event occurred with data:", data);
 * });
 *
 * // Вызываем событие "hover" дважды
 * emitter.emit("hover", "Element hovered");
 * // Output: Hover event occurred with data: Element hovered
 * emitter.emit("hover", "Element hovered again");
 * // No output for the second "hover" event, because the handler was removed after the first invocation
 */
export class EventEmmiter {
	/**
	 * Список событий и связанных с ними обработчиков.
	 */
	Events: Map<string, Set<Callback>> = new Map()

	/**
	 * Метод добавляет обработчик события.
	 * @param eventName - Имя события, к которому будет привязан обработчик.
	 * @param callback - Функция-обработчик, которая будет вызвана при наступлении события.
	 */
	on: (eventName: string, callback: Callback) => void

	constructor() {
		this.on = this.addEventListener
	}

	/**
	 * Метод добавляет обработчик события.
	 * @param eventName - Имя события, к которому будет привязан обработчик.
	 * @param callback - Функция-обработчик, которая будет вызвана при наступлении события.
	 */
	addEventListener(eventName: string, callback: Callback) {
		if (!this.Events.has(eventName)) {
			this.Events.set(eventName, new Set())
		}

		const callbacks = this.Events.get(eventName)

		if (callbacks) {
			callbacks.add(callback)
		}
	}

	/**
	 * Метод удаляет указанный обработчик события.
	 * @param eventName - Имя события, от которого нужно удалить обработчик.
	 * @param callback - Функция-обработчик, который нужно удалить.
	 */
	removeEventListener(eventName: string, callback: Callback) {
		if (!this.Events.has(eventName)) {
			this.Events.set(eventName, new Set())
		}

		const callbacks = this.Events.get(eventName)

		if (callbacks) {
			callbacks.delete(callback)
		}
	}

	/**
	 * Метод удаляет все обработчики указанного события.
	 * @param eventName - Имя события, от которого нужно удалить все обработчики.
	 */
	removeAllEventListeners(eventName: string) {
		if (!this.Events.has(eventName)) {
			this.Events.set(eventName, new Set())
		}
		const callbacks = this.Events.get(eventName)
		if (callbacks) {
			callbacks.clear()
		}
	}

	/**
	 * Метод вызывает все обработчики указанного события с переданными данными.
	 * @param eventName - Имя события, для которого нужно вызвать обработчики.
	 * @param data - Данные, которые будут переданы в обработчики.
	 */
	emit(eventName: string, data?: any) {
		if (!this.Events.has(eventName)) {
			this.Events.set(eventName, new Set())
		}
		const callbacks = this.Events.get(eventName)
		if (callbacks) {
			for (const callback of callbacks) {
				callback(data)
			}
		}
	}

	/**
	 * Метод добавляет обработчик, который будет вызван только один раз
	 * при наступлении указанного события.
	 * @param eventName - Имя события, к которому будет привязан обработчик.
	 * @param callback - Функция-обработчик, которая будет вызвана только один раз.
	 */
	once(eventName: string, callback: Callback) {
		const fn = (data: any) => {
			callback(data)
			this.removeEventListener(eventName, fn)
		}
		this.addEventListener(eventName, fn)
	}
}
